import React from "react";
import { Helmet } from "react-helmet-async";
import { siteConfig, absoluteLogoUrl } from "@/config/siteConfig";

const SEO = ({
  title,
  description,
  path = "/",
  ogImage = "",
  noindex = false,
  extraSchema = null,
  rawTitle = null,
  ogType = "website",
  robotsContent = null,
}) => {
  const fullTitle = rawTitle
    ? rawTitle
    : title
    ? `${title} — ${siteConfig.COMPANY_NAME}`
    : `${siteConfig.COMPANY_NAME} — ${siteConfig.TAGLINE}`;
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const desc =
    description ||
    siteConfig.SECONDARY_TAGLINE;
  // Default OG image: 1200×630 branded share card (AX logo + tagline + Dubai).
  // Callers can override with a page-specific `ogImage` prop.
  const shareImage = ogImage || `${siteConfig.DOMAIN}/brand/og-default.jpg`;
  const shareImageAlt = `${siteConfig.COMPANY_NAME} — ${siteConfig.TAGLINE}`;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteConfig.COMPANY_NAME,
    url: siteConfig.DOMAIN,
    logo: `${siteConfig.DOMAIN}/brand/axion-logo-primary.png`,
    description: siteConfig.TAGLINE,
    address: {
      "@type": "PostalAddress",
      streetAddress: siteConfig.ADDRESS_LINE_1,
      addressLocality: siteConfig.ADDRESS_LOCALITY,
      addressCountry: siteConfig.ADDRESS_COUNTRY,
    },
    sameAs: [siteConfig.LINKEDIN_URL].filter(
      (v) => v && !String(v).startsWith("[")
    ),
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {(noindex || robotsContent) && (
        <meta name="robots" content={robotsContent || "noindex,nofollow"} />
      )}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteConfig.COMPANY_NAME} />
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content={shareImageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={shareImage} />
      <meta name="twitter:image:alt" content={shareImageAlt} />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      {extraSchema && (
        <script type="application/ld+json">{JSON.stringify(extraSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
